import { Portal, normalizeProps, useMachine } from '@zag-js/react';
import * as select from '@zag-js/select';
import { type FC, useEffect, useId } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';
import { IconIds } from 'molecules/Icon/types';

type ItemProps =
  | {
      label: string;
      value: string;
    }
  | string;

interface DropdownProps {
  /**
   * The options to be displayed in the dropdown. Can be an array of `ItemProps` objects, a string of pipe-separated values, or `null` to indicate no options.
   *
   * Each `ItemProps` object should have a `label` and `value` property, representing the display label and underlying value for the dropdown option.
   */
  options?: ItemProps[] | string | null;
  /**
   * Determines whether the dropdown should allow multiple selections.
   */
  multiple?: boolean;
  /**
   * Determines whether the dropdown should be disabled and not interactive.
   */
  disabled?: boolean;
  /**
   * A callback function that is called when the value of the dropdown changes.
   * @param value - The new value of the dropdown.
   */
  onChange?: (value: string | string[]) => void;
  /**
   * The ID of the icon to display in the dropdown.
   */
  icon?: IconIds;
  /**
   * The ID of the icon to display to the left of the dropdown control.
   */
  startIcon?: IconIds;
  /**
   * The default value for the dropdown. If the `multiple` prop is true, this should be an array of strings. If `multiple` is false, this should be a single string.
   */
  defaultValue?: string[];
  /**
   * The value of the dropdown. If the `multiple` prop is true, this should be an array of strings. If `multiple` is false, this should be a single string.
   */
  value?: string[];
  /**
   * An optional placeholder to display below the dropdown control.
   */
  placeholder?: string;
  /**
   * Indicates whether the dropdown has an error state.
   */
  error?: boolean;
}

/**
 * Whether the machine already holds the value the parent is asking for. Without
 * this the effect below re-sends the same value and fires `onValueChange` again.
 */
const isSameValue = (current: string[], next: string[]) =>
  current.length === next.length && current.every((item, index) => item === next[index]);

const generateOptions = (options?: ItemProps[] | string | null) => {
  if (!options) {
    return [];
  }

  const items = typeof options === 'string' ? options.split('|') : options;

  return items.map(item => {
    const value = typeof item === 'string' ? item : item.value;
    const label = typeof item === 'string' ? item : item.label;

    return { label, value };
  });
};

const DropDown: FC<DropdownProps> = ({
  options,
  multiple,
  disabled,
  error,
  icon,
  startIcon,
  defaultValue,
  placeholder,
  onChange,
  value,
}) => {
  const items = generateOptions(options);

  const collection = select.collection({
    items,
  });

  const service = useMachine(select.machine, {
    id: useId(),
    collection,
    multiple,
    disabled,
    // `value` makes this a controlled select, so the machine has to START there.
    // Leaving it to the effect below would make the first sync a real change,
    // and 1.x fires `onValueChange` for a programmatic `setValue` as well: every
    // listing page rewrote its own URL to `?category=All&page=1` on load.
    defaultValue: defaultValue ?? value,
    onValueChange: results => {
      onChange && onChange(multiple ? results.value : results.value[0]);
    },
    onFocusOutside(event) {
      event.preventDefault();
    },
    positioning: {
      sameWidth: true,
      slide: true,
      fitViewport: true,
    },
  });

  const api = select.connect(service, normalizeProps);

  useEffect(() => {
    if (value && !isSameValue(api.value, value)) {
      api.setValue(value);
    }
  }, [value]);

  return (
    <div className="group" data-error={error} {...api.getRootProps()}>
      <button
        {...api.getTriggerProps()}
        className="flex w-full items-center gap-2 rounded-lg border border-primary-1000 bg-gray-25 p-[11px] text-primary-700 focus-within:stateful-outline-tealCustom data-[disabled]:border-gray-50 data-[disabled]:bg-gray-300 data-[error=true]:stateful-outline-error data-[error-true]:focus-within:stateful-outline-error"
        aria-label={placeholder || 'Select an option'}
      >
        {startIcon && <Icon icon={startIcon} size={16} />}
        <input
          className="w-full cursor-pointer bg-transparent text-left text-md text-gray-700 placeholder:text-gray-400 focus-visible:outline-none disabled:cursor-not-allowed"
          placeholder={placeholder || 'Select Option'}
          value={api.valueAsString}
        />
        <Icon
          icon={icon || 'chevron-down'}
          size={24}
          className="stroke-2 transition-transform group-data-[state=open]:rotate-180"
        />
      </button>
      <Portal>
        <div
          {...api.getPositionerProps()}
          className={twJoin('overflow-y-auto rounded-md  bg-white py-2 shadow-xs', api.open ? 'visible' : 'hidden')}
        >
          <ul {...api.getContentProps()} className="flex flex-col outline-none">
            {items.map(item => (
              <li
                key={item.value}
                {...api.getItemProps({ item })}
                className="group flex cursor-pointer select-none items-center justify-between gap-2 bg-transparent px-3.5 py-2.5 text-md text-gray-900 transition-colors hover:bg-primary-100 data-[highlighted]:bg-primary-100 data-[state=checked]:bg-primary-50"
              >
                {item.label}
                {multiple && (
                  <div className="relative flex size-5 min-w-5 items-center justify-center rounded-[6px] border border-primary-1000 text-primary-600 group-data-[state=checked]:border-primary-600">
                    <Icon icon="check" size={14} className="stroke-2 group-data-[state=unchecked]:hidden" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Portal>
    </div>
  );
};

export default DropDown;
